import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Paper,
  Typography as T,
  useMediaQuery,
} from "@mui/material";

import { Spacer } from "components/atoms";
import { toDateOnlyLocal } from "helpers/date";
import {
  pickFirst,
  reduceIconPath,
  resolveVehicleImagePath,
} from "helpers/functions";
import { useCurrency, useTypedTheme } from "hooks";
import { useStoreActions, useStoreState } from "store/store";
import { AddonCart } from "store/types";
import { palette } from "theme/palette";

import { CurrencySelector } from "./CurrencySelector";
import EmptySummary from "./EmptySummary";
import { SummaryAddons } from "./SummaryAddons";
import { SummaryDates } from "./SummaryDates";

export const SummaryWrapper = ({
  plugForFirstStep,
  page6Variant,
  onClose,
}: {
  sectionBoxHeight?: number;
  page6Variant?: boolean;
  plugForFirstStep: boolean;
  onClose?: () => void;
}) => {
  // styles
  const {
    theme: {
      breakpoints: { down },
    },
  } = useTypedTheme();
  const isMobile = useMediaQuery(down("md"));
  // state
  const getFeeThunk = useStoreActions((a) => a.getFeeThunk);

  const { watch } = useFormContext();
  const formStoreSelectedCar = useStoreState((s) => s.selectedCar);
  const coupon = useStoreState((s) => s.coupon);
  const fee = useStoreState((s) => s.fee);
  const isTotalLoading = useStoreState((s) => s.isTotalLoading);
  const selectedAddons = useStoreState((s) => s.selectedAddons);
  const cart = useStoreState((s) => s.cart);
  const { currency, formatPrice } = useCurrency();

  const formSelectedCar = watch("selectedCar");
  const numberOfPassengersRedux = cart?.passengers;
  const isRoundTrip = cart?.roundtrip;

  const location = cart?.location?.name;
  const destination = cart?.destination?.name;

  // Build a key from the effective form values (fallback to cart when form is empty)
  const bookingDateForm = toDateOnlyLocal(watch("bookingDate"));
  const bookingTimeForm = watch("bookingTime") ?? "";
  const departureDateForm = toDateOnlyLocal(watch("departureDate"));
  const departureTimeForm = watch("departureTime") ?? "";

  const bookingDateEff =
    bookingDateForm ?? toDateOnlyLocal(cart?.booking_date ?? null);
  const bookingTimeEff = bookingTimeForm || cart?.booking_time || "";

  const departureDateEff =
    departureDateForm ?? toDateOnlyLocal(cart?.departure_date ?? null);
  const departureTimeEff = departureTimeForm || cart?.departure_time || "";

  // Changing any of these will remount SummaryDates
  const summaryDatesKey = [
    bookingDateEff?.getTime() ?? "",
    bookingTimeEff,
    departureDateEff?.getTime() ?? "",
    departureTimeEff,
  ].join("|");

  const selectedCar = pickFirst([formSelectedCar, formStoreSelectedCar]);
  const numberOfPassengers = pickFirst([
    watch("numberOfPassengers"),
    numberOfPassengersRedux,
  ]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [numberOfCars, setNumberOfCars] = useState(0);
  const [oneSeatRuleBroken, setOneSeatRuleBroken] = useState(false);
  const currencyExchangeFeeAmount = useMemo(() => {
    if (!fee?.currency_exchange_fee) {
      return 0;
    }

    const parsed = Number(String(fee.currency_exchange_fee).replace(/,/g, ""));

    return Number.isFinite(parsed) ? parsed : 0;
  }, [fee?.currency_exchange_fee]);

  const hasCurrencyExchangeFee = currencyExchangeFeeAmount > 0;

  const passengersToSend = watch("numberOfPassengers") ?? cart?.passengers;
  const vehicleToSend =
    watch("selectedCar")?.vehicleId ?? formStoreSelectedCar?.vehicleId;

  useEffect(() => {
    if (selectedCar?.numberOfSeats && numberOfPassengers) {
      const passengerCoefficient =
        numberOfPassengers / selectedCar.numberOfSeats;
      setNumberOfCars(Math.ceil(passengerCoefficient));
    }
  }, [selectedCar, numberOfPassengers]);

  useEffect(() => {
    setOneSeatRuleBroken(
      selectedCar?.oneSeatAllowed &&
        parseFloat(numberOfPassengers) !== 1 &&
        numberOfPassengers !== ""
    );
  }, [selectedCar?.oneSeatAllowed, numberOfPassengers]);

  useEffect(() => {
    const params: {
      addons?: AddonCart[];
      passengers?: number;
      vehicle_id?: number;
      roundtrip?: number;
      coupon_id?: number;
    } = {};

    if (selectedAddons?.length) {
      params.addons = selectedAddons;
    }

    if (passengersToSend) {
      params.passengers = Number(passengersToSend) ?? 1;
    }

    if (vehicleToSend) {
      params.vehicle_id = Number(vehicleToSend);
    }

    if (coupon?.coupon_id) {
      params.coupon_id = Number(coupon.coupon_id);
    }

    if (typeof isRoundTrip !== "undefined") {
      params.roundtrip = Number(isRoundTrip);
    }

    if (params.vehicle_id || params.addons) {
      getFeeThunk(params);
    }
  }, [
    currency,
    getFeeThunk,
    passengersToSend,
    isRoundTrip,
    selectedAddons,
    vehicleToSend,
    coupon?.coupon_id,
  ]);

  const calculateSavings = (): string => {
    if (!coupon || !fee) return "";
    if (
      !coupon?.amount ||
      !coupon?.coupon_id ||
      !coupon?.discount_type ||
      !fee?.subtotal
    ) {
      return "";
    }

    if (coupon.discount_type === "percentage") {
      const savings =
        (Number(fee.subtotal ?? 0) * Number(coupon.amount ?? 0)) / 100;
      return formatPrice(savings.toFixed(2));
    } else if (coupon.discount_type === "flat") {
      return formatPrice(Number(coupon.amount ?? 0));
    }
    return "";
  };

  return (
    <Paper
      elevation={page6Variant ? 0 : 10}
      sx={{
        ...(page6Variant
          ? {}
          : {
              position: "sticky",
              top: 0,
            }),
        padding: page6Variant ? 0 : ["16px", "48px", "48px", "48px"],
        paddingTop: page6Variant ? 4 : "24px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
        flexBasis: "20%",
        ...(isMobile && {
          borderRadius: "16px 16px 0 0",
          [".MuiDrawer-modal &"]: {
            width: [
              "calc(100% - 32px)",
              "calc(100% - 96px)",
              "calc(100% - 96px)",
              "calc(100% - 96px)",
            ],
          },
        }),
      }}
    >
      <T
        variant="h1"
        style={{ marginBottom: plugForFirstStep || isMobile ? 0 : "16px" }}
      >
        Order Summary
      </T>
      {plugForFirstStep && <Spacer marginTop={25} />}
      <Box
        ref={scrollRef}
        sx={{
          "&::-webkit-scrollbar": {
            width: 7,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "accent",
            borderRadius: 5,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: palette.grey,
          },
        }}
      >
        {plugForFirstStep ? null : <SummaryDates key={summaryDatesKey} />}
        {selectedCar && selectedCar?.carName && !plugForFirstStep ? (
          oneSeatRuleBroken ? (
            <Box
              sx={{
                boxSizing: "border-box",
                borderRadius: "20px",
                width: "100%",
                padding: "26px",
              }}
            >
              <T variant="h4" color={palette.warning}>
                Only one seat allowed for this type of vehicle
              </T>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                borderRadius: "20px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: isMobile ? "18px" : "24px",
                }}
              >
                <T
                  variant="h4"
                  sx={{ maxWidth: "70%", marginTop: "24px", flexGrow: 2 }}
                >
                  {isRoundTrip
                    ? `Round-trip trip from ${location} to ${destination}`
                    : `One way trip from ${location} to ${destination}`}
                </T>
                <Box
                  sx={{
                    padding: "11px 14px",
                    borderRadius: "10px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: palette.grey,
                  }}
                >
                  <T variant="h3">{formatPrice(selectedCar.price)}</T>
                </Box>
              </Box>
              <Box
                sx={{
                  padding: "16px 10px 16px 10px",
                  border: `1px solid ${palette.grey}`,
                  borderRadius: "10px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",

                    alignItems: "center",
                  }}
                >
                  <img
                    src={resolveVehicleImagePath(selectedCar.picturePath)}
                    style={{
                      width: "86px",
                      height: "47px",
                      objectFit: "contain",
                    }}
                    alt={"selected vehicle order summary photo"}
                  />
                  <Box
                    sx={{
                      marginLeft: "18px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box>
                      <T
                        variant="cardLabelText"
                        sx={{
                          fontSize: isMobile ? "12px" : "14px",
                          lineHeight: isMobile ? "13.8px" : "16.1px",
                        }}
                      >
                        Vehicle:
                      </T>
                      <T
                        variant="secondaryText"
                        sx={{
                          fontSize: isMobile ? "12px" : "14px",
                          lineHeight: isMobile ? "13.8px" : "16.1px",
                          marginLeft: "6px",
                        }}
                      >
                        {numberOfCars > 1
                          ? `${selectedCar.carName} (${numberOfCars})`
                          : selectedCar.carName}
                      </T>
                    </Box>
                    <Box
                      sx={{
                        marginTop: "2px",
                      }}
                    >
                      <T
                        variant="cardLabelText"
                        sx={{
                          fontSize: isMobile ? "12px" : "14px",
                          lineHeight: isMobile ? "13.8px" : "16.1px",
                        }}
                      >
                        Passengers:
                      </T>
                      {numberOfPassengers ? (
                        <>
                          <img
                            src={reduceIconPath("Person.svg")}
                            style={{
                              marginLeft: "6px",
                              marginRight: "4px",
                              marginBottom: "-1px",
                            }}
                          />
                          <T
                            variant="secondaryText"
                            sx={{
                              fontSize: isMobile ? "12px" : "13px",
                              lineHeight: isMobile ? "13.8px" : "16.1px",
                            }}
                          >
                            {numberOfPassengers}
                          </T>
                        </>
                      ) : (
                        <T
                          variant="secondaryText"
                          color={palette.warning}
                          sx={{
                            marginLeft: "6px",
                          }}
                        >
                          Please enter the number of passengers
                        </T>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  backgroundColor: palette.darkGrey,
                  height: "1px",
                  marginTop: "24px",
                }}
              />
              <SummaryAddons addonsToDisplay={selectedAddons} />
              <Box
                sx={{
                  backgroundColor: palette.accent,
                  color: palette.white,
                  padding: "17px 24px 19px 24px",
                  borderRadius: "10px",
                  position: "relative",
                  marginTop: "24px",
                }}
              >
                <CurrencySelector />
                {isTotalLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress
                      style={{ color: "rgba(255, 255, 255, .38)" }}
                    />
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <T variant="cardLabelText" color="inherit">
                        Subtotal
                      </T>
                      <T variant="secondaryText" color="inherit">
                        {formatPrice(fee?.subtotal)}
                      </T>
                    </Box>
                    {fee && (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: "7px",
                          }}
                        >
                          <T variant="cardLabelText" color="inherit">
                            Fees
                          </T>
                          <T variant="secondaryText" color="inherit">
                            {formatPrice(fee.tax)}
                          </T>
                        </Box>
                        {hasCurrencyExchangeFee && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginTop: "7px",
                            }}
                          >
                            <T variant="cardLabelText" color="inherit">
                              Currency exch.
                            </T>
                            <T variant="secondaryText" color="inherit">
                              {formatPrice(currencyExchangeFeeAmount)}
                            </T>
                          </Box>
                        )}
                      </>
                    )}
                    {calculateSavings() && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginTop: "7px",
                        }}
                      >
                        <T variant="cardLabelText" color="inherit">
                          Coupon Savings
                        </T>
                        <T variant="secondaryText" color="inherit">
                          {`- ${calculateSavings()}`}
                        </T>
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginTop: "7px",
                      }}
                    >
                      <T variant="h5" color="inherit">
                        TOTAL
                      </T>
                      <T variant="h4" color="inherit">
                        {formatPrice(fee?.total)}
                      </T>
                    </Box>
                  </>
                )}
              </Box>
              {isMobile && !page6Variant && (
                <ButtonBase
                  sx={{
                    height: "48px",
                    borderColor: "rgba(183, 183, 183, 1)",
                    marginTop: "80px",
                    borderRadius: "8px",
                    width: "100%",
                    marginBottom: "24px",
                    border: "2px solid rgba(183, 183, 183, 1)",
                  }}
                  onClick={onClose}
                >
                  <T
                    variant="h5sb"
                    sx={{
                      color: "rgba(183, 183, 183, 1)",
                      marginRight: 1,
                      fontSize: [16, 18],
                    }}
                  >
                    Close
                  </T>
                </ButtonBase>
              )}
            </Box>
          )
        ) : !plugForFirstStep &&
          (cart?.booking_date || cart?.departure_date) ? null : (
          <EmptySummary
            onClose={onClose}
            mobileVariant={isMobile}
            page6Variant={page6Variant}
          />
        )}
      </Box>
    </Paper>
  );
};
